import { apiDelete, apiGet, apiPatch, apiPost } from './http-client';

export const EMBED_AUTH_ME_API = {
  me: '/api/v1/auth/me',
  meChangePassword: '/api/v1/auth/me/change-password',
  setInitialPassword: '/api/v1/auth/me/set-initial-password'
} as const;

export type TrainerPlacementEntry = {
  id: number;
  branch_id: number;
  branch_name: string;
};

export type MeMembershipEntry = {
  organization_id: number;
  role: string;
  organization_name: string | null;
  organization_slug: string | null;
};

/** Контекст входа из токена: раздел, студия и роль. */
export type MeLoginContext = {
  surface: 'client' | 'trainer' | 'staff' | string;
  org_id: number;
  role: string;
  label: string;
};

/** Read-only снимок с backend (`domain.FitnessTrainerRuntimeView`); не RBAC-источник. */
export type FitnessTrainerRuntimeView = {
  schema_version: number;
  staff_core_role_canonical: string;
  org_staff_line_role_as_issued: string;
  legacy_trainer_slug: boolean;
  fitness_trainer_persona: boolean;
  trainer_placement_count: number;
};

export type MeResponse = {
  id: number;
  email: string;
  /** Нормализованный номер (если есть в профиле); смена через отдельные сценарии входа/студии. */
  phone?: string | null;
  full_name: string | null;
  role: string;
  organization_id: number | null;
  date_of_birth: string | null;
  is_parent_for_minors: boolean;
  trainer_placements: TrainerPlacementEntry[] | null;
  /** Персона «тренер fitness» (модуль), не вторая core-роль; см. `domain.HasFitnessTrainerPersona` (core). */
  fitness_trainer_persona?: boolean;
  /** Паритет со снимком capabilities в `GET /auth/me`; `null` вне org staff-линии. */
  fitness_trainer_runtime?: FitnessTrainerRuntimeView | null;
  active_branch_id: number | null;
  memberships: MeMembershipEntry[] | null;
  /** Организации пользователя (slug/name) — есть в ответе `/auth/me`. */
  organizations?: { id: number; name: string; slug: string }[] | null;
  /** Контекст входа из токена; null у старых сессий без контекста. */
  context?: MeLoginContext | null;
  /** Есть другие разделы/студии — показываем «Сменить роль». */
  has_other_contexts?: boolean;
  email_verified_at: string | null;
  phone_verified_at: string | null;
  /** false — пароль ещё не задан (карточку завёл сотрудник): клиент задаёт пароль при онбординге. */
  password_set?: boolean;
  /** Источник создания карточки (self|admin|trainer|superadmin|platform_staff|tenant_request|yclients). */
  created_via?: string | null;
  /** Сервер сообщает, настроен ли почтовый шлюз для подтверждения email. */
  email_verification_available?: boolean;
  avatar_url: string | null;
  notify_via_telegram?: boolean | null;
  notify_via_email?: boolean | null;
  telegram_id?: string | null;
  modules?: Record<string, boolean> | null;
  capabilities?: {
    permissions?: string[];
    permissions_effective?: string[];
    permissions_preset?: string[];
    jit_permissions?: string[];
    modules_effective?: Record<string, boolean> | null;
    capability_version?: number;
    routes_allowed?: string[] | null;
    deny_reasons?: Record<string, unknown> | null;
    staff_admin_shell?: { allowed: boolean; deny?: Record<string, unknown> | null } | null;
    org_tariff_write?: { allowed: boolean; code?: string; detail?: string } | null;
  } | null;
};

export type MeProfilePatch = {
  full_name?: string | null;
  email?: string | null;
  date_of_birth?: string | null;
  avatar_url?: string | null;
  /** Только для роли client. */
  is_parent_for_minors?: boolean | null;
  notify_via_telegram?: boolean | null;
  notify_via_email?: boolean | null;
  telegram_id?: string | null;
};

export type TelegramLinkResponse = {
  linked: boolean;
  pending: boolean;
  telegram_id: string | null;
  bot_username: string | null;
  connect_url: string | null;
  expires_at: string | null;
  telegram_configured?: boolean;
  max_configured?: boolean;
};

export type TelegramLinkStartBody = {
  branch_id?: number | null;
};

export type SelectClientOrganizationBody = {
  organization_id: number;
};

export type AuthTokenPairResponse = {
  access_token: string;
  refresh_token?: string | null;
  token_type: string;
};

/** GET /api/v1/auth/me */
export function getMe(accessToken: string, orgSlug?: string | null): Promise<MeResponse> {
  const query = orgSlug ? { org_slug: orgSlug } : undefined;
  return apiGet<MeResponse>(EMBED_AUTH_ME_API.me, query, { accessToken });
}

/** PATCH /api/v1/auth/me */
export function patchMeProfile(body: MeProfilePatch, accessToken: string): Promise<MeResponse> {
  return apiPatch<MeResponse>(EMBED_AUTH_ME_API.me, body, { accessToken });
}

/**
 * DELETE /api/v1/auth/me — самостоятельное удаление аккаунта.
 * Технически выполняется анонимизация: профиль обезличивается, история бронирований
 * у организации сохраняется. После успеха все сессии отозваны — нужен повторный вход.
 */
export function deleteMeAccount(accessToken: string): Promise<void> {
  return apiDelete<void>(EMBED_AUTH_ME_API.me, { accessToken });
}

/**
 * POST /api/v1/auth/me/leave-organization — выйти из одной организации,
 * сохранив аккаунт и доступ к остальным студиям.
 */
export function postLeaveOrganization(accessToken: string, organizationId: number): Promise<void> {
  return apiPost<void>(
    '/api/v1/auth/me/leave-organization',
    { organization_id: organizationId },
    { accessToken }
  );
}

export function getTelegramLink(accessToken: string): Promise<TelegramLinkResponse> {
  return apiGet<TelegramLinkResponse>('/api/v1/auth/me/telegram-link', undefined, { accessToken });
}

export function postTelegramLink(body: TelegramLinkStartBody, accessToken: string): Promise<TelegramLinkResponse> {
  return apiPost<TelegramLinkResponse>('/api/v1/auth/me/telegram-link', body, { accessToken });
}

/** POST /api/v1/auth/request-email-verification — повторная отправка письма подтверждения email. */
export function postRequestEmailVerification(accessToken: string): Promise<void> {
  return apiPost<void>('/api/v1/auth/request-email-verification', {}, { accessToken });
}

/** POST /api/v1/auth/resend-email-verification — публичный повтор письма (гейт парольного входа). 204 всегда. */
export function postPublicResendEmailVerification(email: string): Promise<void> {
  return apiPost<void>('/api/v1/auth/resend-email-verification', { email });
}

export type MeChangePasswordBody = {
  /** Бэкенд ждёт `old_password` (см. POST /api/v1/auth/me/change-password). */
  old_password: string;
  new_password: string;
  new_password_confirm?: string;
  /** Токен Yandex SmartCaptcha (когда captcha_enabled=true). */
  captcha_token?: string;
};

/** POST /api/v1/auth/me/change-password — смена пароля при известном текущем. */
export function postChangeMePassword(body: MeChangePasswordBody, accessToken: string): Promise<void> {
  return apiPost<void>(EMBED_AUTH_ME_API.meChangePassword, body, { accessToken });
}

/**
 * POST /api/v1/auth/me/set-initial-password — задать первый пароль клиенту,
 * карточку которого создал сотрудник (email/пароля ещё нет). Успех — 204.
 * Ошибки: 403 (не клиент), 409 `password_already_set`, 422 (слабый пароль: ≥12, строчная+прописная+цифра).
 */
export function postSetInitialPassword(password: string, accessToken: string): Promise<void> {
  return apiPost<void>(EMBED_AUTH_ME_API.setInitialPassword, { password }, { accessToken });
}

/** POST /api/v1/auth/me/select-client-organization — новая пара JWT с выбранной организацией (клиент). */
export function postSelectClientOrganization(
  accessToken: string,
  body: SelectClientOrganizationBody
): Promise<AuthTokenPairResponse> {
  return apiPost<AuthTokenPairResponse>('/api/v1/auth/me/select-client-organization', body, { accessToken });
}

/** POST /api/v1/auth/me/feedback — обращение в поддержку платформы или администратору организации. */
export type AuthMeFeedbackPayload = {
  recipient: 'developers' | 'org_admin';
  category: 'improvement' | 'complaint' | 'freeform';
  body_md: string;
  is_anonymous: boolean;
};

export function postAuthMeFeedback(accessToken: string, body: AuthMeFeedbackPayload): Promise<Record<string, unknown>> {
  return apiPost<Record<string, unknown>>('/api/v1/auth/me/feedback', body, { accessToken });
}
